import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, MapPin, Star, Search, Pencil, ExternalLink, Ban, Check, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminSchools() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", province: "", district: "", short_intro: "" });

  const [editing, setEditing] = useState(null); // school being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`${V1}/schools?limit=500&admin=true`).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post(`${V1}/schools`, form);
      toast.success("Okul oluşturuldu");
      setOpen(false); setForm({ name: "", province: "", district: "", short_intro: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };

  const toggleFeatured = async (s) => {
    await api.patch(`${V1}/schools/${s.id}`, { featured: !s.featured });
    load();
  };
  const setStatus = async (s, status) => {
    await api.patch(`${V1}/admin/schools/${s.id}/status`, { status });
    toast.success(status === "suspended" ? "Askıya alındı" : "Onaylandı");
    load();
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      name: s.name || "",
      province: s.province || "",
      district: s.district || "",
      address: s.address || "",
      phone: s.phone || "",
      email: s.email || "",
      website: s.website || "",
      short_intro: s.short_intro || "",
      long_description: s.long_description || "",
      cover_url: s.cover_url || "",
      logo_url: s.logo_url || "",
      brochure_url: s.brochure_url || "",
      gallery: (s.gallery || []).join("\n"),
      education_levels: (s.education_levels || []).join(", "),
      facilities: (s.facilities || []).join(", "),
      tuition_min: s.tuition_min || "",
      boarding: !!s.boarding,
      foreign_language: !!s.foreign_language,
      featured: !!s.featured,
      is_published: s.is_published !== false,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        province: editForm.province,
        district: editForm.district,
        address: editForm.address,
        phone: editForm.phone,
        email: editForm.email,
        website: editForm.website,
        short_intro: editForm.short_intro,
        long_description: editForm.long_description,
        cover_url: editForm.cover_url,
        logo_url: editForm.logo_url,
        brochure_url: editForm.brochure_url,
        gallery: editForm.gallery.split("\n").map((s) => s.trim()).filter(Boolean),
        education_levels: editForm.education_levels.split(",").map((s) => s.trim()).filter(Boolean),
        facilities: editForm.facilities.split(",").map((s) => s.trim()).filter(Boolean),
        tuition_min: editForm.tuition_min ? parseInt(editForm.tuition_min) : null,
        boarding: editForm.boarding,
        foreign_language: editForm.foreign_language,
        featured: editForm.featured,
        is_published: editForm.is_published,
      };
      await api.patch(`${V1}/schools/${editing.id}`, payload);
      toast.success("Okul güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (s.name || "").toLowerCase().includes(t)
      || (s.province || "").toLowerCase().includes(t)
      || (s.district || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6" data-testid="admin-schools-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Yönetim</div>
          <h1 className="font-display text-3xl font-bold">Okullar</h1>
          <p className="text-muted-foreground text-sm mt-1">{filtered.length} / {items.length} okul</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-school-btn"><Plus className="h-4 w-4" /> Yeni Okul</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Okul Oluştur</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Okul Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-form-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>İl</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="school-form-province" /></div>
                <div><Label>İlçe</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="school-form-district" /></div>
              </div>
              <div><Label>Kısa Tanıtım</Label><Input value={form.short_intro} onChange={(e) => setForm({ ...form, short_intro: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={create} data-testid="save-school-btn">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Okul, il veya ilçe ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-school-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 border border-border rounded-md bg-background" data-testid="admin-school-status-filter">
          <option value="all">Tüm durumlar</option>
          <option value="approved">Onaylı</option>
          <option value="pending">Beklemede</option>
          <option value="suspended">Askıda</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((s) => (
          <div key={s.id} className="p-5 rounded-xl border border-border bg-card flex gap-4" data-testid={`admin-school-row-${s.id}`}>
            {s.cover_url ? (
              <img src={s.cover_url} alt="" className="h-20 w-24 object-cover rounded" />
            ) : (
              <div className="h-20 w-24 bg-muted rounded grid place-content-center text-xs text-muted-foreground">Görsel yok</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.province} / {s.district}</div>
              <div className="font-display font-bold truncate">{s.name}</div>
              <div className="mt-2 flex gap-1.5 items-center flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-medium ${s.status === "approved" ? "bg-success/10 text-success" : s.status === "suspended" ? "bg-destructive/10 text-destructive" : "bg-muted"}`}>{s.status}</span>
                <Button size="sm" variant="ghost" onClick={() => toggleFeatured(s)} title="Öne çıkar" data-testid={`toggle-featured-${s.id}`}>
                  <Star className={`h-3.5 w-3.5 ${s.featured ? "text-accent fill-accent" : ""}`} />
                </Button>
                <Button size="sm" variant="outline" asChild data-testid={`view-school-${s.id}`}>
                  <Link to={`/okul/${s.slug}`} target="_blank"><ExternalLink className="h-3.5 w-3.5" /> Detay</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Düzenle
                </Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(s, s.status === "approved" ? "suspended" : "approved")} data-testid={`toggle-status-${s.id}`}>
                  {s.status === "approved" ? <><Ban className="h-3.5 w-3.5" /> Askıya Al</> : <><Check className="h-3.5 w-3.5" /> Onayla</>}
                </Button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-2 p-10 text-center text-muted-foreground">Filtreye uygun okul yok</div>}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="edit-school-dialog">
          <DialogHeader>
            <DialogTitle>Okul Bilgilerini Düzenle</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Okul Adı</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" /></div>
              <div><Label>İl</Label><Input value={editForm.province} onChange={(e) => setEditForm({ ...editForm, province: e.target.value })} data-testid="edit-province" /></div>
              <div><Label>İlçe</Label><Input value={editForm.district} onChange={(e) => setEditForm({ ...editForm, district: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Adres</Label><Textarea rows={2} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><Label>E-posta</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Website</Label><Input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Kısa Tanıtım</Label><Textarea rows={2} value={editForm.short_intro} onChange={(e) => setEditForm({ ...editForm, short_intro: e.target.value })} data-testid="edit-short-intro" /></div>
              <div className="md:col-span-2"><Label>Uzun Açıklama (Markdown)</Label><Textarea rows={5} value={editForm.long_description} onChange={(e) => setEditForm({ ...editForm, long_description: e.target.value })} className="font-mono text-xs" /></div>
              <div><Label>Kapak Görseli URL</Label><Input value={editForm.cover_url} onChange={(e) => setEditForm({ ...editForm, cover_url: e.target.value })} /></div>
              <div><Label>Logo URL</Label><Input value={editForm.logo_url} onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Broşür PDF URL</Label><Input value={editForm.brochure_url} onChange={(e) => setEditForm({ ...editForm, brochure_url: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Galeri (her satıra bir URL)</Label><Textarea rows={4} value={editForm.gallery} onChange={(e) => setEditForm({ ...editForm, gallery: e.target.value })} className="font-mono text-xs" /></div>
              <div><Label>Eğitim Kademeleri (virgülle)</Label><Input value={editForm.education_levels} onChange={(e) => setEditForm({ ...editForm, education_levels: e.target.value })} placeholder="ortaokul, lise" /></div>
              <div><Label>Olanaklar (virgülle)</Label><Input value={editForm.facilities} onChange={(e) => setEditForm({ ...editForm, facilities: e.target.value })} /></div>
              <div><Label>Başlangıç Ücret (₺/yıl)</Label><Input type="number" value={editForm.tuition_min} onChange={(e) => setEditForm({ ...editForm, tuition_min: e.target.value })} /></div>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.boarding} onCheckedChange={(v) => setEditForm({ ...editForm, boarding: v })} /> Yatılı</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.foreign_language} onCheckedChange={(v) => setEditForm({ ...editForm, foreign_language: v })} /> Yabancı Dil</label>
              </div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.featured} onCheckedChange={(v) => setEditForm({ ...editForm, featured: v })} /> Öne Çıkar</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.is_published} onCheckedChange={(v) => setEditForm({ ...editForm, is_published: v })} /> Yayında</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Vazgeç</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-school-edit-btn"><Save className="h-4 w-4" /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
